import { useState, useEffect } from 'react';
// Extension build flow: generateExtension only — no integration binding or permissions.
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Chrome, 
  ArrowLeft, 
  ArrowRight,
  CheckCircle,
  Settings,
  Download,
  Palette,
  Shield,
  Plus,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import apiService, { SpaceSocket } from '../../../services/api';
import { defaultExtensionDisplayNameFromSocket, isSocketUuid } from '../../../utils/socketUtils';

interface ExtensionBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  spaceId: string;
}

interface ExtensionConfig {
  name: string;
  description: string;
  icon: string;
  themeColor: string;
}

interface GeneratedExtension {
  filename: string;
  downloadUrl: string;
  size: string;
}

export default function ExtensionBuilderModal({
  isOpen,
  onClose,
  spaceId
}: ExtensionBuilderModalProps) {
  const { t } = useTranslation();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [selectedSocket, setSelectedSocket] = useState<SpaceSocket | null>(null);
  const [extensionConfig, setExtensionConfig] = useState<ExtensionConfig>({
    name: '',
    description: '',
    icon: '🧠',
    themeColor: '#007AFF'
  });

  // Data loading state
  const [availableSockets, setAvailableSockets] = useState<SpaceSocket[]>([]);
  const [loadingSockets, setLoadingSockets] = useState(true);
  const [socketsError, setSocketsError] = useState<string | null>(null);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedExtension, setGeneratedExtension] = useState<GeneratedExtension | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Pre-made icon options
  const iconOptions = ['🧠', '💼', '📝', '🔖', '⚡', '🎯', '🏢', '📱'];
  
  // Color options
  const colorOptions = [
    '#007AFF', // Blue
    '#8B5CF6', // Purple  
    '#10B981', // Green
    '#F59E0B', // Orange
    '#EF4444'  // Red
  ];

  // Load available API sockets when modal opens
  useEffect(() => {
    if (isOpen && spaceId) {
      loadAvailableSockets();
    }
  }, [isOpen, spaceId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setSelectedSocket(null);
      setExtensionConfig({
        name: '',
        description: '',
        icon: '🧠',
        themeColor: '#007AFF'
      });
      setGeneratedExtension(null);
      setGenerationError(null);
    }
  }, [isOpen]);

  const loadAvailableSockets = async () => {
    try {
      setLoadingSockets(true);
      setSocketsError(null);
      
      const socketList = await apiService.getSocketsForSpace(spaceId);
      
      // Filter for API sockets only
      const apiSockets = socketList.sockets.filter(socket => {
        const socketType = socket.socket_config?.template_id || 
                          (socket.socket_config as any)?.type ||
                          'api';
        return socketType === 'api' && isSocketUuid(socket.socket_id);
      });
      
      setAvailableSockets(apiSockets);
    } catch (error) {
      console.error('Failed to load sockets:', error);
      setSocketsError('Failed to load available sockets');
    } finally {
      setLoadingSockets(false);
    }
  };

  const handleSocketSelect = (socket: SpaceSocket) => {
    setSelectedSocket(socket);
    
    // Auto-fill extension name based on socket
    if (!extensionConfig.name) {
      const name = defaultExtensionDisplayNameFromSocket(socket);
      const spaceName = name.replace(/\s+Extension$/i, '').trim() || socket.socket_name.trim();
      setExtensionConfig(prev => ({
        ...prev,
        name,
        description: `Quick memory capture for ${spaceName.toLowerCase()}-related content`
      }));
    }
  };

  const handleNextStep = () => {
    if (currentStep === 1 && isSocketUuid(selectedSocket?.socket_id)) {
      setCurrentStep(2);
    } else if (currentStep === 2 && extensionConfig.name.trim() && isSocketUuid(selectedSocket?.socket_id)) {
      handleGenerate();
    }
  };

  const handleBackStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else if (currentStep === 3) {
      setCurrentStep(2);
    }
  };

  const handleGenerate = async () => {
    const socketId = selectedSocket?.socket_id?.trim() ?? '';
    if (!isSocketUuid(socketId) || !extensionConfig.name.trim()) return;

    setCurrentStep(3);
    setIsGenerating(true);
    setGenerationError(null);

    try {
      // Generate extension using real API
      const result = await apiService.generateExtension({
        socket_id: socketId,
        extension_config: {
          name: extensionConfig.name,
          description: extensionConfig.description,
          icon: extensionConfig.icon,
          theme_color: extensionConfig.themeColor
        }
      });

      if (result.success && result.extension) {
        const generatedExtension: GeneratedExtension = {
          filename: result.extension.filename,
          size: result.extension.size,
          downloadUrl: result.extension.download_url
        };

        setGeneratedExtension(generatedExtension);
      } else {
        throw new Error(result.message || 'Extension generation failed');
      }
      
      toast.success(t('chromeExtension.messages.generationSuccess', { ns: 'integrations' }));
    } catch (error) {
      console.error('Extension generation failed:', error);
      setGenerationError('Failed to generate extension. Please try again.');
      toast.error(t('chromeExtension.messages.generationFailed', { ns: 'integrations' }));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (generatedExtension) {
      // Create download link
      const link = document.createElement('a');
      link.href = generatedExtension.downloadUrl;
      link.download = generatedExtension.filename;
      link.click();
      
      toast.success(t('chromeExtension.messages.downloadStarted', { ns: 'integrations' }));
    }
  };

  const handleStartOver = () => {
    setCurrentStep(1);
    setSelectedSocket(null);
    setExtensionConfig({
      name: '',
      description: '',
      icon: '🧠',
      themeColor: '#007AFF'
    });
    setGeneratedExtension(null);
    setGenerationError(null);
  };

  const steps = [
    { id: 1, label: t('chromeExtension.steps.selectSocket', { ns: 'integrations' }), complete: currentStep > 1 },
    { id: 2, label: t('chromeExtension.steps.customize', { ns: 'integrations' }), complete: currentStep > 2 },
    { id: 3, label: t('chromeExtension.steps.download', { ns: 'integrations' }), complete: currentStep > 3 }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent variant="scrollable" className="max-w-6xl">
        <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
          <DialogTitle className="flex items-center gap-2">
            <Chrome className="w-6 h-6 text-blue-600" />
            {t('chromeExtension.builder.title', { ns: 'integrations' })}
          </DialogTitle>
          <DialogDescription>
            {t('chromeExtension.builder.description', { ns: 'integrations' })}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="mx-6 mb-4 flex flex-shrink-0 items-center gap-4 rounded-lg bg-gray-50 p-4">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center gap-2 ${
                currentStep === step.id ? 'text-blue-600 font-semibold' : 
                step.complete ? 'text-green-600' : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  currentStep === step.id ? 'border-blue-600 bg-blue-50' :
                  step.complete ? 'border-green-600 bg-green-50' : 'border-gray-300'
                }`}>
                  {step.complete ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <span className="text-sm font-medium">{step.id}</span>
                  )}
                </div>
                <span>{step.label}</span>
              </div>
              {index < steps.length - 1 && (
                <ArrowRight className="w-4 h-4 text-gray-400 mx-3" />
              )}
            </div>
          ))}
        </div>

        <DialogScrollArea className="min-h-[300px] px-6">
        {/* Step Content */}
        <div>
          <AnimatePresence mode="wait">
            {/* Step 1: Socket Selection */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    {t('chromeExtension.builder.selectSocket.title', { ns: 'integrations' })}
                  </h3>
                  <p className="text-gray-600">
                    {t('chromeExtension.builder.selectSocket.description', { ns: 'integrations' })}
                  </p>
                </div>

                {socketsError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-red-900 mb-1">
                          {t('chromeExtension.builder.errors.loadSocketsTitle', { ns: 'integrations' })}
                        </h4>
                        <p className="text-sm text-red-700">{socketsError}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={loadAvailableSockets}
                          className="mt-2"
                        >
                          {t('retry', { ns: 'common' })}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle>
                      {t('chromeExtension.builder.selectSocket.availableSockets', { ns: 'integrations' })}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingSockets ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
                        <span>{t('chromeExtension.builder.loading', { ns: 'integrations' })}</span>
                      </div>
                    ) : availableSockets.length === 0 ? (
                      <div className="text-center py-12">
                        <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h4 className="font-medium text-gray-900 mb-2">
                          {t('chromeExtension.builder.selectSocket.noSockets', { ns: 'integrations' })}
                        </h4>
                        <p className="text-gray-600 mb-4">
                          {t('chromeExtension.builder.selectSocket.noSocketsDescription', { ns: 'integrations' })}
                        </p>
                        <Button variant="outline">
                          <Plus className="w-4 h-4 mr-2" />
                          {t('chromeExtension.builder.selectSocket.createSocket', { ns: 'integrations' })}
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {availableSockets.map((socket) => (
                          <div
                            key={socket.socket_id}
                            onClick={() => handleSocketSelect(socket)}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              selectedSocket?.socket_id === socket.socket_id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                            }`}
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <Shield className="w-6 h-6 text-blue-600" />
                              <div className="flex-1">
                                <h4 className="font-medium">{socket.socket_name}</h4>
                                <p className="text-xs text-gray-500">{socket.socket_id}</p>
                              </div>
                              {selectedSocket?.socket_id === socket.socket_id && (
                                <CheckCircle className="w-5 h-5 text-blue-600" />
                              )}
                            </div>
                            <div className="space-y-1 text-sm text-gray-600">
                              <div>{t('chromeExtension.builder.selectSocket.active', { ns: 'integrations' })}: {socket.is_active ? t('yes', { ns: 'common' }) : t('no', { ns: 'common' })}</div>
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${
                                  socket.is_active ? 'bg-green-500' : 'bg-gray-400'
                                }`} />
                                <span>{socket.is_active ? t('active', { ns: 'common' }) : t('inactive', { ns: 'common' })}</span>
                              </div>
                            </div>
                            <div className="mt-3">
                              <Badge variant={selectedSocket?.socket_id === socket.socket_id ? "default" : "secondary"}>
                                {selectedSocket?.socket_id === socket.socket_id 
                                  ? t('chromeExtension.builder.selectSocket.selected', { ns: 'integrations' })
                                  : t('chromeExtension.builder.selectSocket.selectSocket', { ns: 'integrations' })
                                }
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step 2: Customization */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8"
              >
                {/* Left Column - Configuration */}
                <div className="space-y-6">
                  {/* Extension Details */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        {t('chromeExtension.builder.customize.extensionDetails', { ns: 'integrations' })}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          {t('chromeExtension.builder.customize.extensionName', { ns: 'integrations' })} *
                        </label>
                        <Input
                          value={extensionConfig.name}
                          onChange={(e) => setExtensionConfig(prev => ({ ...prev, name: e.target.value }))}
                          placeholder={t('chromeExtension.builder.customize.extensionNamePlaceholder', { ns: 'integrations' })}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {t('chromeExtension.builder.customize.extensionNameHelp', { ns: 'integrations' })}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          {t('chromeExtension.builder.customize.description', { ns: 'integrations' })}
                        </label>
                        <Input
                          value={extensionConfig.description}
                          onChange={(e) => setExtensionConfig(prev => ({ ...prev, description: e.target.value }))}
                          placeholder={t('chromeExtension.builder.customize.descriptionPlaceholder', { ns: 'integrations' })}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Icon Selection */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Palette className="w-4 h-4" />
                        {t('chromeExtension.builder.customize.chooseIcon', { ns: 'integrations' })}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-2 mb-4">
                        {iconOptions.map((icon) => (
                          <button
                            key={icon}
                            onClick={() => setExtensionConfig(prev => ({ ...prev, icon }))}
                            className={`p-3 text-xl border-2 rounded-lg hover:border-blue-500 transition-colors ${
                              extensionConfig.icon === icon ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                            }`}
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">
                        {t('chromeExtension.builder.customize.selectedIcon', { ns: 'integrations' })}: {extensionConfig.icon}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Theme Color */}
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        {t('chromeExtension.builder.customize.themeColor', { ns: 'integrations' })}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-2 mb-4">
                        {colorOptions.map((color) => (
                          <button
                            key={color}
                            onClick={() => setExtensionConfig(prev => ({ ...prev, themeColor: color }))}
                            className={`w-8 h-8 rounded-full border-2 ${
                              extensionConfig.themeColor === color ? 'border-gray-800' : 'border-gray-300'
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={extensionConfig.themeColor}
                          onChange={(e) => setExtensionConfig(prev => ({ ...prev, themeColor: e.target.value }))}
                          className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                        />
                        <Input
                          value={extensionConfig.themeColor}
                          onChange={(e) => setExtensionConfig(prev => ({ ...prev, themeColor: e.target.value }))}
                          placeholder="#007AFF"
                          className="flex-1"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column - Preview */}
                <div className="space-y-6">
                  {/* Live Preview */}
                  <Card className="sticky top-4">
                    <CardHeader>
                      <CardTitle>
                        {t('chromeExtension.builder.customize.livePreview', { ns: 'integrations' })}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                        {/* Extension Popup Preview */}
                        <div className="bg-white p-3 rounded-lg border shadow-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">{extensionConfig.icon}</span>
                            <span className="font-medium">{extensionConfig.name || t('chromeExtension.builder.customize.extensionNameDefault', { ns: 'integrations' })}</span>
                          </div>
                          <div className="text-sm text-gray-600 mb-2">
                            {t('chromeExtension.builder.customize.connectedTo', { ns: 'integrations' })}: {selectedSocket?.socket_name}
                          </div>
                          <div className="flex gap-2">
                            <button className="p-2 bg-gray-100 rounded text-xs">📄</button>
                            <button className="p-2 bg-gray-100 rounded text-xs">✂️</button>
                            <button className="p-2 bg-gray-100 rounded text-xs">🔖</button>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            {t('chromeExtension.builder.customize.capturesTypes', { ns: 'integrations' })}
                          </p>
                        </div>

                        {/* Browser Toolbar Preview */}
                        <div className="bg-gray-100 p-2 rounded">
                          <div className="bg-white p-2 rounded flex items-center justify-between text-xs">
                            <span className="text-gray-400">chrome://extensions/</span>
                            <div 
                              className="w-6 h-6 rounded flex items-center justify-center text-white text-sm"
                              style={{ backgroundColor: extensionConfig.themeColor }}
                            >
                              {extensionConfig.icon}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Socket Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        {t('chromeExtension.builder.customize.socketInfo', { ns: 'integrations' })}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">
                            {t('chromeExtension.builder.customize.socketName', { ns: 'integrations' })}:
                          </span>
                          <Badge variant="secondary">{selectedSocket?.socket_name}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">
                            {t('chromeExtension.builder.customize.socketId', { ns: 'integrations' })}:
                          </span>
                          <code className="text-xs bg-gray-100 px-1 rounded">{selectedSocket?.socket_id}</code>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">
                            {t('chromeExtension.builder.customize.socketType', { ns: 'integrations' })}:
                          </span>
                          <Badge variant="outline">API Socket</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            )}

            {/* Step 3: Generation & Download */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                {isGenerating ? (
                  // Generation in Progress
                  <div className="text-center py-12">
                    <div className="mb-6">
                      <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      <h3 className="text-xl font-semibold mb-2">
                        {t('chromeExtension.builder.generate.generating', { ns: 'integrations' })}
                      </h3>
                      <p className="text-gray-600">
                        {t('chromeExtension.builder.generate.generatingDescription', { ns: 'integrations' })}
                      </p>
                    </div>
                    
                    <div className="max-w-md mx-auto">
                      <div className="bg-gray-200 rounded-full h-2 mb-2">
                        <motion.div 
                          className="bg-blue-600 h-2 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: '100%' }}
                          transition={{ duration: 3 }}
                        />
                      </div>
                      <p className="text-sm text-gray-500">
                        {t('chromeExtension.builder.generate.pleaseWait', { ns: 'integrations' })}
                      </p>
                    </div>
                  </div>
                ) : generationError ? (
                  // Generation Error
                  <div className="text-center py-12">
                    <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-red-800 mb-2">
                      {t('chromeExtension.builder.generate.error', { ns: 'integrations' })}
                    </h3>
                    <p className="text-red-600 mb-6">{generationError}</p>
                    <div className="flex gap-2 justify-center">
                      <Button variant="outline" onClick={handleBackStep}>
                        {t('back', { ns: 'common' })}
                      </Button>
                      <Button onClick={handleGenerate} className="bg-blue-600 hover:bg-blue-700">
                        {t('retry', { ns: 'common' })}
                      </Button>
                    </div>
                  </div>
                ) : generatedExtension ? (
                  // Generation Complete
                  <div className="space-y-6">
                    <div className="text-center">
                      <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold text-green-800 mb-2">
                        {t('chromeExtension.builder.generate.success', { ns: 'integrations' })}
                      </h3>
                      <p className="text-gray-600">
                        {t('chromeExtension.builder.generate.successDescription', { ns: 'integrations' })}
                      </p>
                    </div>

                    {/* Download Card */}
                    <Card className="border-green-200 bg-green-50">
                      <CardContent className="p-6">
                        <div className="text-center mb-6">
                          <h4 className="text-lg font-semibold mb-2">
                            🎉 {t('chromeExtension.builder.generate.downloadReady', { ns: 'integrations' })}
                          </h4>
                          <div className="bg-white p-4 rounded-lg border inline-block">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-2xl">{extensionConfig.icon}</span>
                              <div>
                                <div className="font-medium">{generatedExtension.filename}</div>
                                <div className="text-sm text-gray-600">
                                  {t('chromeExtension.builder.generate.size', { ns: 'integrations' })}: {generatedExtension.size}
                                </div>
                              </div>
                            </div>
                            <Button onClick={handleDownload} className="bg-blue-600 hover:bg-blue-700">
                              <Download className="w-4 h-4 mr-2" />
                              {t('chromeExtension.builder.generate.downloadExtension', { ns: 'integrations' })}
                            </Button>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 justify-center">
                          <Button variant="outline" size="sm">
                            📧 {t('chromeExtension.builder.generate.emailLink', { ns: 'integrations' })}
                          </Button>
                          <Button variant="outline" size="sm">
                            <Settings className="w-4 h-4 mr-1" />
                            {t('chromeExtension.builder.generate.manage', { ns: 'integrations' })}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Extension Summary */}
                    <Card>
                      <CardHeader>
                        <CardTitle>
                          {t('chromeExtension.builder.generate.summary', { ns: 'integrations' })}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-sm text-gray-600">
                              {t('chromeExtension.builder.generate.extensionName', { ns: 'integrations' })}:
                            </span>
                            <div className="font-medium">{extensionConfig.name}</div>
                          </div>
                          <div>
                            <span className="text-sm text-gray-600">
                              {t('chromeExtension.builder.generate.icon', { ns: 'integrations' })}:
                            </span>
                            <div className="font-medium">{extensionConfig.icon}</div>
                          </div>
                          <div>
                            <span className="text-sm text-gray-600">
                              {t('chromeExtension.builder.generate.color', { ns: 'integrations' })}:
                            </span>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-4 h-4 rounded"
                                style={{ backgroundColor: extensionConfig.themeColor }}
                              />
                              <span className="font-medium">{extensionConfig.themeColor}</span>
                            </div>
                          </div>
                          <div>
                            <span className="text-sm text-gray-600">
                              {t('chromeExtension.builder.generate.socket', { ns: 'integrations' })}:
                            </span>
                            <div className="font-medium">{selectedSocket?.socket_name}</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ) : null}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        </DialogScrollArea>

        {/* Navigation Buttons */}
        {currentStep < 3 && (
          <DialogFooter variant="scrollable" className="justify-between px-6 pb-6">
            <Button 
              variant="outline" 
              onClick={currentStep === 1 ? onClose : handleBackStep}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {currentStep === 1 
                ? t('cancel', { ns: 'common' })
                : t('back', { ns: 'common' })
              }
            </Button>
            <Button 
              onClick={handleNextStep}
              disabled={
                (currentStep === 1 && !isSocketUuid(selectedSocket?.socket_id)) ||
                (currentStep === 2 && (!extensionConfig.name.trim() || !isSocketUuid(selectedSocket?.socket_id)))
              }
              className="bg-blue-600 hover:bg-blue-700"
            >
              {currentStep === 2 ? (
                <>
                  <Chrome className="w-4 h-4 mr-2" />
                  {t('chromeExtension.builder.generate.generateExtension', { ns: 'integrations' })}
                </>
              ) : (
                <>
                  {t('continue', { ns: 'common' })}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </DialogFooter>
        )}

        {/* Generation Complete Navigation */}
        {currentStep === 3 && generatedExtension && (
          <DialogFooter variant="scrollable" className="justify-between px-6 pb-6">
            <Button variant="outline" onClick={onClose}>
              🏠 {t('chromeExtension.builder.generate.backToDashboard', { ns: 'integrations' })}
            </Button>
            <Button onClick={handleStartOver} className="bg-blue-600 hover:bg-blue-700">
              ➕ {t('chromeExtension.builder.generate.createAnother', { ns: 'integrations' })}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
